import * as React from "react";

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DoubleChevLeftIcon,
  DoubleChevRightIcon,
  MoreIcon
} from "@/assets/icons";
import { ButtonProps, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  siblings?: number;
  boundaries?: number;
  className?: string;
}

const DOTS = "...";

/**
 * Generate pagination range with dots
 */
const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, index) => index + start);
};

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("flex", className)}
    {...props}
  />
);
Pagination.displayName = "Pagination";

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex flex-row items-center gap-1", className)}
    {...props}
  />
));
PaginationContent.displayName = "PaginationContent";

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
));
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
} & ButtonProps;

const PaginationLink = ({
  className,
  isActive,
  size = "icon",
  ...props
}: PaginationLinkProps) => (
  <button
    aria-current={isActive ? "page" : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? "outline" : "ghost",
        size
      }),
      className
    )}
    {...props}
  />
);
PaginationLink.displayName = "PaginationLink";

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="default"
    className={cn("gap-1 pl-2.5", className)}
    {...props}
  >
    <ArrowLeftIcon className="h-4 w-4 fill-txtBody" />
    <span>Previous</span>
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    size="default"
    className={cn("gap-1 pr-2.5", className)}
    {...props}
  >
    <span>Next</span>
    <ArrowRightIcon className="h-4 w-4 fill-txtBody" />
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreIcon className="w-4 h-4 rotate-90 fill-txtBody" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export default function PaginationComponent({
  currentPage,
  totalPages,
  onPageChange,
  siblings = 1,
  boundaries = 1,
  className
}: PaginationProps) {
  const handlePageChange = (pageNumber: number | string) => {
    if (pageNumber !== DOTS && pageNumber !== currentPage) {
      onPageChange(Number(pageNumber));
    }
  };

  const renderPageNumbers = () => {
    const totalNumbers = siblings * 2 + boundaries * 2 + 3; // siblings + boundaries + current page + 2 dots

    // Show all pages if total is small
    if (totalPages <= totalNumbers) {
      return range(1, totalPages);
    }

    // Calculate the main group (current page and siblings)
    const leftSibling = Math.max(currentPage - siblings, boundaries + 1);
    const rightSibling = Math.min(
      currentPage + siblings,
      totalPages - boundaries
    );

    // Should show dots on left side
    const shouldShowLeftDots = leftSibling > boundaries + 1;
    // Should show dots on right side
    const shouldShowRightDots = rightSibling < totalPages - boundaries;

    // Case 1: No left dots, but right dots
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftRange = range(1, 4); // Always show first 4 numbers in initial case
      const rightBoundary = range(totalPages - boundaries + 1, totalPages);
      return [...leftRange, DOTS, ...rightBoundary];
    }

    // Case 2: No right dots, but left dots
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const leftBoundary = range(1, boundaries);
      const rightRange = range(rightSibling - siblings, totalPages);
      return [...leftBoundary, DOTS, ...rightRange];
    }

    // Case 3: Both left and right dots
    if (shouldShowLeftDots && shouldShowRightDots) {
      const leftBoundary = range(1, boundaries);
      const middleRange = range(leftSibling, rightSibling);
      const rightBoundary = range(totalPages - boundaries + 1, totalPages);
      return [...leftBoundary, DOTS, ...middleRange, DOTS, ...rightBoundary];
    }

    return range(1, totalPages);
  };

  const pages = renderPageNumbers();

  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationLink
            aria-label="First Page"
            onClick={() => handlePageChange(1)}
            className={cn(
              "w-8 h-10 p-0 border border-gainsboro cursor-pointer",
              currentPage === 1 && "pointer-events-none opacity-50"
            )}
            aria-disabled={currentPage === 1}
          >
            <DoubleChevLeftIcon className="h-4 w-4 fill-txtBody" />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem>
          <PaginationLink
            aria-label="Previous Page"
            onClick={() => handlePageChange(currentPage - 1)}
            className={cn(
              "w-8 h-10 p-0 border border-gainsboro mr-4 cursor-pointer",
              currentPage === 1 && "pointer-events-none opacity-50"
            )}
            aria-disabled={currentPage === 1}
          >
            <ArrowLeftIcon className="h-4 w-4 fill-txtBody" />
          </PaginationLink>
        </PaginationItem>

        {pages.map((pageNumber, index) =>
          pageNumber === DOTS ? (
            <PaginationItem key={index}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={index}>
              <PaginationLink
                aria-label={`Go to page ${pageNumber}`}
                onClick={() => handlePageChange(pageNumber)}
                isActive={pageNumber === currentPage}
                className={cn(
                  "w-10 h-10 border-0 cursor-pointer",
                  pageNumber === currentPage && "bg-teal-50"
                )}
              >
                {pageNumber}
              </PaginationLink>
            </PaginationItem>
          )
        )}

        <PaginationItem>
          <PaginationLink
            aria-label="Next Page"
            onClick={() => handlePageChange(currentPage + 1)}
            className={cn(
              "w-8 h-10 p-0 border border-gainsboro ml-4 cursor-pointer",
              currentPage === totalPages && "pointer-events-none opacity-50"
            )}
            aria-disabled={currentPage === totalPages}
          >
            <ArrowRightIcon className="h-4 w-4 fill-txtBody" />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem>
          <PaginationLink
            aria-label="Last Page"
            onClick={() => handlePageChange(totalPages)}
            className={cn(
              "w-8 h-10 p-0 border border-gainsboro cursor-pointer",
              currentPage === totalPages && "pointer-events-none opacity-50"
            )}
            aria-disabled={currentPage === totalPages}
          >
            <DoubleChevRightIcon />
          </PaginationLink>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

export {
  Pagination,
  PaginationComponent,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
};
